import {
  Box,
  Image,
  Text,
  Heading,
  Button,
  HStack,
  Flex,
  Stack,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import { StarIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";

import FitmentBadge from "../components/FitmentBadge";
import { Part } from "../Types/part";
import { mapPartToProduct } from "../utils/mapPartToProduct";
import { fetchPartById } from "../Api/parts.service";
import { useAddPartReviewMutation } from "../Api/partsApi";
import GlobalSpinner from "../../components/CustomReusables/GlobalSpinner";

const PartDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [part, setPart] = useState<Part | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState<string | undefined>();

  // ⭐ Reviews
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [addReview, { isLoading: submitting }] = useAddPartReviewMutation();

  useEffect(() => {
    const loadPart = async () => {
      try {
        if (!id) return;
        const data = await fetchPartById(id);
        setPart(data);
        setSelectedImage(data.images?.[0]);
      } catch {
        setPart(null);
      } finally {
        setLoading(false);
      }
    };

    loadPart();
  }, [id]);

  const handleAddReview = async () => {
    if (!part || !reviewMessage) return;

    await addReview({
      id: part._id,
      review: {
        message: reviewMessage,
        rating: reviewRating,
      },
    });

    setReviewMessage("");
    setReviewRating(5);

    // Reload part to get updated reviews + rating
    const updated = await fetchPartById(part._id);
    setPart(updated);
  };

  if (loading) return <GlobalSpinner />;
  if (!part) return <Text>Part not found</Text>;

  return (
    <Flex gap={10} flexWrap="wrap">
      {/* LEFT — IMAGE GALLERY */}
      <Box>
        <Image
          src={selectedImage}
          boxSize="400px"
          objectFit="cover"
          borderRadius="md"
          mb={4}
        />

        <HStack spacing={2}>
          {part.images.map((img, idx) => (
            <Image
              key={idx}
              src={img}
              boxSize="70px"
              objectFit="cover"
              cursor="pointer"
              borderRadius="md"
              border={
                selectedImage === img ? "2px solid teal" : "1px solid #ddd"
              }
              onClick={() => setSelectedImage(img)}
            />
          ))}
        </HStack>
      </Box>

      {/* RIGHT — DETAILS */}
      <Box flex="1">
        <Heading size="lg">{part.title}</Heading>

        <FitmentBadge partId={part._id} />

        {/* ⭐ Rating summary */}
        <HStack spacing={1} mt={2}>
          {Array.from({ length: 5 }).map((_, i) => (
            <StarIcon
              key={i}
              color={
                i < Math.round(part.rating || 0) ? "yellow.400" : "gray.300"
              }
            />
          ))}
          <Text fontSize="sm" color="gray.500">
            ({part.reviewCount || 0} reviews)
          </Text>
        </HStack>

        <Text fontSize="xl" color="teal.500" fontWeight="bold" mt={3}>
          R{part.price}
        </Text>

        <Text mt={2}>Part Number: {part.partNumber}</Text>

        <Text mt={2}>
          Stock: {part.stock > 0 ? part.stock : "Out of stock"}
        </Text>

        <Button
          mt={5}
          colorScheme="teal"
          isDisabled={part.stock <= 0}
          onClick={() => dispatch(addToCart(mapPartToProduct(part)))}
        >
          Add to Cart
        </Button>

        {/* WHY IT FITS */}
        <Box mt={6}>
          <Heading size="sm" mb={2}>
            Why this part fits your car
          </Heading>
          <Text fontSize="sm" color="gray.600">
            This part is compatible with your vehicle’s engine, model year, and
            OEM fitment specifications.
          </Text>
        </Box>

        {/* REVIEWS */}
        <Box mt={8}>
          <Heading size="md" mb={3}>
            Customer Reviews
          </Heading>

          {part.reviews?.length ? (
            part.reviews.map((review, idx) => (
              <Box key={idx} p={4} borderWidth="1px" borderRadius="md" mb={3}>
                <HStack spacing={1} mb={2}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon
                      key={i}
                      color={i < review.rating ? "yellow.400" : "gray.300"}
                    />
                  ))}
                </HStack>
                <Text>{review.message}</Text>
              </Box>
            ))
          ) : (
            <Text color="gray.500">No reviews yet. Be the first!</Text>
          )}

          {/* ADD REVIEW */}
          <Box mt={4}>
            <Textarea
              placeholder="Write your review..."
              value={reviewMessage}
              onChange={(e) => setReviewMessage(e.target.value)}
              mb={2}
            />

            <HStack mb={3}>
              <Text>Rating:</Text>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  cursor="pointer"
                  color={reviewRating >= star ? "yellow.400" : "gray.300"}
                  onClick={() => setReviewRating(star)}
                />
              ))}
            </HStack>

            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleAddReview}
              isLoading={submitting}
            >
              Submit Review
            </Button>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default PartDetailsPage;
